import { useState, useEffect, useCallback } from "react";
import { supabase } from "../lib/supabase";

export const useTeamMembers = (availableForBookingOnly = true, locationId = null) => {
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadTeamMembers = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Base query for team members
      let query = supabase
        .from("team_members")
        .select("*")
        .eq("is_active", true)
        .order("order_index", { ascending: true });

      // Filter for booking availability if requested
      if (availableForBookingOnly) {
        query = query.eq("is_available_for_booking", true);
      }

      const { data: teamData, error: teamError } = await query;
      if (teamError) throw teamError;

      // Get team member locations via the junction table
      const { data: teamLocationData, error: teamLocationError } = await supabase
        .from("team_member_locations")
        .select(`
          team_member_id,
          locations (
            id,
            title,
            address,
            phone,
            schedule
          )
        `);

      if (teamLocationError) throw teamLocationError;

      // Combine the data
      let teamMembersWithLocations = teamData.map(member => {
        const memberLocations = teamLocationData
          .filter(tl => tl.team_member_id === member.id)
          .map(tl => tl.locations);
        
        return {
          ...member,
          team_locations: memberLocations,
          // Keep legacy fields for backward compatibility
          location_id: memberLocations.length > 0 ? memberLocations[0].id : null,
          locations: memberLocations.length > 0 ? memberLocations[0] : null
        };
      });

      // Filter by specific location if requested
      if (locationId) {
        teamMembersWithLocations = teamMembersWithLocations.filter(member => 
          member.team_locations.some(location => location.id === locationId)
        );
      }

      setTeamMembers(teamMembersWithLocations || []);
    } catch (err) {
      console.error("Error loading team members:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [availableForBookingOnly, locationId]);

  useEffect(() => {
    loadTeamMembers();
  }, [loadTeamMembers]);

  const refreshTeamMembers = () => {
    loadTeamMembers();
  };

  // Helper function to format schedule for display
  const formatMemberSchedule = (member) => {
    if (!member.availability_schedule) return "Program nespecificat";
    
    const dayNames = {
      monday: 'Luni',
      tuesday: 'Marți',
      wednesday: 'Miercuri',
      thursday: 'Joi',
      friday: 'Vineri',
      saturday: 'Sâmbătă',
      sunday: 'Duminică'
    };

    const scheduleEntries = Object.entries(member.availability_schedule || {})
      .filter(([day, slots]) => slots && slots.length > 0)
      .map(([day, slots]) => {
        const timeSlots = slots.map(slot => `${slot.start}-${slot.end}`).join(', ');
        return `${dayNames[day]}: ${timeSlots}`;
      });

    return scheduleEntries.length > 0 ? scheduleEntries.join(' | ') : "Fără program disponibil";
  };

  // Helper function to get locations where team members work
  const getAvailableLocations = () => {
    const locationMap = new Map();
    
    teamMembers.forEach(member => {
      member.team_locations?.forEach(location => {
        if (!locationMap.has(location.id)) {
          locationMap.set(location.id, {
            ...location,
            doctorCount: 1
          });
        } else {
          const existing = locationMap.get(location.id);
          locationMap.set(location.id, {
            ...existing,
            doctorCount: existing.doctorCount + 1
          });
        }
      });
    });
    
    return Array.from(locationMap.values()).sort((a, b) => a.title.localeCompare(b.title));
  };

  // Helper function to check if member works at specific location
  const memberWorksAtLocation = (member, locationId) => {
    return member.team_locations?.some(location => location.id === locationId) || false;
  };

  return {
    teamMembers,
    loading,
    error,
    refreshTeamMembers,
    formatMemberSchedule,
    getAvailableLocations,
    memberWorksAtLocation
  };
};

export default useTeamMembers; 